import { Dispatch, SetStateAction } from 'react'
import { match } from 'ts-pattern'

import { LineItemGroup, Section } from '@sequencehq/core-components'
import type { Currency } from '@sequencehq/api'
import type { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'

import { CreditNoteRevenueRecognitionMethod } from 'CreditNotes/types'
import { MAX_INPUT_TOTAL, MAX_QUANTITY } from 'CreditNotes/constants'
import { RevRecLineItemValidationHints } from 'CreditNotes/components/RevRecLineItemValidationHints'
import { parseFloatOrZero } from 'lib/parsers'

import { ExceedsRemainingHint } from './ExceedsRemainingHint'
import { lineItemNet } from './lineItemNet'

type CreditNoteLineItemRateDisplay =
  Dashboardv99990101CommonModels.CreditNoteLineItems.CreditNoteLineItemRateDisplay

export type CreditNoteLineItemRow = {
  /** Stable client-side identity for the row (React key, edit targeting). */
  rowId: string
  /** Source invoice line item id. Undefined for manually added line items. */
  lineItemId?: string
  /** Carried over from the source invoice line item when it has one. */
  productId?: string
  groupId: string
  title: string
  rate: string
  taxRate: string
  checked: boolean
  qty: string
  price: string
  /** Fields below are only populated for manually added line items. */
  description?: string
  rateDisplay?: CreditNoteLineItemRateDisplay
  servicePeriod?: { start: string; endInclusive: string }
  revenueRecognitionMethod?: CreditNoteRevenueRecognitionMethod
}

type Props = {
  rows: CreditNoteLineItemRow[]
  setRows: Dispatch<SetStateAction<CreditNoteLineItemRow[]>>
  currency: Currency
  validationTriggered: boolean
  /**
   * Rows exceeding their invoice line item's remaining creditable amount,
   * keyed by rowId with that remaining amount. The parent owns the math
   * (overCreditedRows); the table only renders the inline hint.
   */
  overCreditByRowId: Map<string, string>
  onAddLineItem: () => void
  onEditLineItem: (row: CreditNoteLineItemRow) => void
}

export const CreditNoteLineItemTable = ({
  rows,
  setRows,
  currency,
  validationTriggered,
  overCreditByRowId,
  onAddLineItem,
  onEditLineItem
}: Props) => {
  const updateRow = (rowId: string, patch: Partial<CreditNoteLineItemRow>) =>
    setRows(prev => prev.map(r => (r.rowId === rowId ? { ...r, ...patch } : r)))

  const deleteRow = (rowId: string) =>
    setRows(prev => prev.filter(r => r.rowId !== rowId))

  // Editing quantity keeps the rate; price is derived as rate × quantity.
  const handleChangeQuantity = (
    row: CreditNoteLineItemRow,
    quantity: string
  ) => {
    const limitedQuantity =
      parseFloatOrZero(quantity) > MAX_QUANTITY ? row.qty : quantity

    updateRow(row.rowId, {
      qty: limitedQuantity,
      price: lineItemNet(row.rate, limitedQuantity)
    })
  }

  // The API derives the line total from rate × quantity, so an edited total must
  // be expressed as rate (with quantity collapsed to 1) to take effect. Mirrors
  // CreditNotes/components/CreditNoteLineItems.tsx.
  const handleChangeTotal = (row: CreditNoteLineItemRow, total: string) => {
    const parsedTotal = parseFloatOrZero(total)
    const limitedTotal = parsedTotal > MAX_INPUT_TOTAL ? row.price : total

    // rate must be derived from the capped total, otherwise a value above
    // MAX_INPUT_TOTAL would display capped but submit the uncapped amount
    // (the API recomputes net as rate × quantity, with quantity collapsed to 1).
    updateRow(row.rowId, {
      price: limitedTotal,
      qty: '1',
      rate: parseFloatOrZero(limitedTotal).toString()
    })
  }

  const groupSelected = match(rows.filter(row => row.checked).length)
    .with(0, () => false as const)
    .with(rows.length, () => true as const)
    .otherwise(() => 'indeterminate' as const)

  return (
    <Section title="Items to credit" marginBottom={0}>
      <LineItemGroup
        variant="creditNotes"
        currency={currency}
        selected={groupSelected}
        onChangeSelected={selected =>
          setRows(prev => prev.map(row => ({ ...row, checked: selected })))
        }
      >
        {rows.map(row => (
          <LineItemGroup.EditableLineItem
            key={row.rowId}
            testIdPrefix={`issue-credit-note.row.${row.rowId}.`}
            title={row.title}
            quantity={row.qty}
            rate={row.rate}
            rateDisplay={row.rateDisplay}
            taxRate={row.taxRate}
            netTotal={row.price}
            selected={row.checked}
            inputsDisabled={!row.checked}
            onChangeSelected={selected =>
              updateRow(row.rowId, { checked: selected })
            }
            onChangeQuantity={quantity => handleChangeQuantity(row, quantity)}
            onChangeTotal={total => handleChangeTotal(row, total)}
            onTriggerAdvancedEdit={() => onEditLineItem(row)}
            onDelete={() => deleteRow(row.rowId)}
            subtitleContent={
              <>
                <ExceedsRemainingHint
                  remainingNet={overCreditByRowId.get(row.rowId)}
                  currency={currency}
                />
                <RevRecLineItemValidationHints
                  showHints={validationTriggered && row.checked}
                  servicePeriod={row.servicePeriod}
                  revenueRecognitionMethod={row.revenueRecognitionMethod}
                  onTriggerAdvancedEdit={() => onEditLineItem(row)}
                />
              </>
            }
          />
        ))}
        <LineItemGroup.AddLineItem
          data-testid="issue-credit-note.addLineItem"
          onClick={onAddLineItem}
        />
      </LineItemGroup>
    </Section>
  )
}
